import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import type { ThemeColor } from "@/types/database"

const tisaCardVariants = cva(
  "card-child rounded-2xl border bg-card text-card-foreground shadow-child-soft transition-all duration-300",
  {
    variants: {
      variant: {
        default: "bg-card",
        outline: "border-2",
        filled: "border-0",
        glass: "bg-card/80 backdrop-blur-sm",
      },
      size: {
        sm: "p-4",
        md: "p-6",
        lg: "p-8",
        xl: "p-10",
      },
      interactive: {
        true: "card-child-interactive cursor-pointer hover:-translate-y-1 hover:shadow-child-strong",
        false: "",
      },
      elevation: {
        none: "shadow-none",
        soft: "shadow-child-soft",
        medium: "shadow-child-medium",
        strong: "shadow-child-strong",
      },
      theme: {
        default: "",
        blue: "theme-blue",
        green: "theme-green",
        yellow: "theme-yellow",
        red: "theme-red",
        purple: "theme-purple",
        orange: "theme-orange",
      },
      glow: {
        true: "relative before:absolute before:inset-0 before:rounded-inherit before:bg-gradient-to-r before:from-primary/20 before:to-secondary/20 before:blur-xl before:-z-10",
        false: "",
      }
    },
    defaultVariants: {
      variant: "default",
      size: "md",
      interactive: false,
      elevation: "soft",
      theme: "default",
      glow: false,
    },
  }
)

export interface TISACardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof tisaCardVariants> {
  themeColor?: ThemeColor
  header?: React.ReactNode
  footer?: React.ReactNode
  icon?: React.ReactNode
  asButton?: boolean
}

const TISACard = React.forwardRef<HTMLDivElement, TISACardProps>(
  ({
    className,
    variant,
    size,
    interactive,
    elevation,
    theme,
    glow,
    themeColor,
    header,
    footer,
    icon,
    asButton = false,
    children,
    onClick,
    ...props
  }, ref) => {
    // Use themeColor prop to override theme variant
    const themeVariant = themeColor || theme

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (onClick) {
        // Add press animation for interactive cards
        const card = e.currentTarget
        card.classList.add('active:scale-95')

        // Haptic feedback
        if ('vibrate' in navigator) {
          navigator.vibrate(30)
        }

        onClick(e)
      }
    }

    const cardProps = {
      className: cn(tisaCardVariants({
        variant,
        size,
        interactive: interactive || !!onClick,
        elevation,
        theme: themeVariant,
        glow
      }), className),
      ref,
      onClick: handleClick,
      role: asButton || onClick ? "button" : undefined,
      tabIndex: asButton || onClick ? 0 : undefined,
      ...props
    }

    return (
      <div {...cardProps}>
        {header && (
          <div className="mb-4 flex items-center gap-3">
            {icon && (
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                {icon}
              </div>
            )}
            <div className="flex-1">{header}</div>
          </div>
        )}

        <div className="space-y-4">
          {children}
        </div>

        {footer && (
          <div className="mt-4 pt-4 border-t border-gray-200/50">
            {footer}
          </div>
        )}
      </div>
    )
  }
)
TISACard.displayName = "TISACard"

// Specialized card components for common use cases
const BookCard = React.forwardRef<HTMLDivElement, TISACardProps & {
  title: string
  author: string
  coverUrl?: string
  theme: ThemeColor
  available?: boolean
}>(({ title, author, coverUrl, theme, available = true, ...props }, ref) => {
  return (
    <TISACard
      ref={ref}
      themeColor={theme}
      interactive
      elevation="medium"
      className={cn(
        "max-w-sm",
        !available && "opacity-60 grayscale"
      )}
      {...props}
    >
      <div className="aspect-[3/4] mb-3 overflow-hidden rounded-lg bg-muted">
        {coverUrl ? (
          <img
            src={coverUrl}
            alt={`Cover of ${title}`}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-muted to-muted-foreground/20">
            <div className="text-center">
              <div className="text-4xl mb-2">📚</div>
              <div className="text-xs text-muted-foreground">No Cover</div>
            </div>
          </div>
        )}
      </div>
      <div className="space-y-2">
        <h3 className="font-child-display font-semibold text-child-base leading-tight line-clamp-2">
          {title}
        </h3>
        <p className="text-child-sm text-muted-foreground">
          by {author}
        </p>
        {!available && (
          <div className="flex items-center gap-1 text-destructive">
            <div className="h-2 w-2 rounded-full bg-destructive"></div>
            <span className="text-child-xs font-medium">Checked Out</span>
          </div>
        )}
      </div>
    </TISACard>
  )
})
BookCard.displayName = "BookCard"

const AchievementCard = React.forwardRef<HTMLDivElement, TISACardProps & {
  title: string
  description: string
  icon: string
  unlocked?: boolean
  coinReward?: number
}>(({ title, description, icon, unlocked = false, coinReward, ...props }, ref) => {
  return (
    <TISACard
      ref={ref}
      variant={unlocked ? "filled" : "outline"}
      elevation={unlocked ? "strong" : "soft"}
      glow={unlocked}
      className={cn(
        "text-center",
        unlocked ? "bg-gradient-to-br from-tisa-yellow-100 to-tisa-orange-100" : "opacity-60"
      )}
      {...props}
    >
      <div className="space-y-3">
        <div className={cn(
          "mx-auto flex h-16 w-16 items-center justify-center rounded-full text-3xl",
          unlocked
            ? "bg-gradient-to-br from-tisa-yellow-400 to-tisa-orange-400 text-white shadow-lg"
            : "bg-muted text-muted-foreground"
        )}>
          {icon}
        </div>
        <div>
          <h3 className="font-child-display font-semibold text-child-lg">
            {title}
          </h3>
          <p className="text-child-sm text-muted-foreground mt-1">
            {description}
          </p>
        </div>
        {coinReward && (
          <div className="tisacoin mx-auto inline-flex">
            🪙 {coinReward}
          </div>
        )}
      </div>
    </TISACard>
  )
})
AchievementCard.displayName = "AchievementCard"

export { TISACard, BookCard, AchievementCard, tisaCardVariants }